import { useEffect, useRef, useState, type PointerEvent as EventoPuntero } from 'react';
import type { Recurso } from '../../api/ops/pagos';
import type { Posicion } from '../../api/ops/sala';
import { Icono } from '../../componentes/Icono';
import { cajaDe } from '../sala/logica';
import { CapaDecoracion, type PiezaEnSala } from './CapaDecoracion';
import {
    MAX_TEXTO, PASO_REJILLA, ajustar, cabeOtroPunto, cajaDePieza, claveDeco, errorDeNombre, estaCerrada, giroDelAsa, indiceDeClave, puntosAbiertos, puntosDe, tamanoDelAsa,
    type Punto,
} from './decoracion';

/**
 * Lienzo del editor de plano (8.6): la sala con su decoración (debajo) y cada recurso en su sitio, con su forma. En el
 * editor, un recurso o una pieza de decoración se elige con un toque y se arrastra (puntero o dedo) en la rejilla del plano
 * (1 a 10.000, enteros); con «Elegir varias», arrastrar una mueve todas las elegidas. La pared elegida enseña sus puntos
 * (arrastrar mueve uno, tocarlo lo elige, el «+» de un tramo añade uno en medio) y las demás piezas, el asa de giro. Una
 * pared a medio dibujar recoge los toques del lienzo. El texto se escribe en una cajita encima del plano (al ponerlo, con
 * dos toques o con «Escribir en el plano»), con el contador de 40. Fuera del editor, solo se mira.
 */
export interface EdicionDecoracion {
    conRejilla: boolean;
    /** Pared a medio dibujar (sus puntos) o `null`. */
    pared: readonly Punto[] | null;
    onPuntoPared: (p: Punto) => void;
    onCerrarPared: () => void;
    puntoElegido: number | null;
    onElegirPunto: (i: number | null) => void;
    onMoverPunto: (indice: number, i: number, p: Punto) => void;
    onAnadirPunto: (indice: number, tramo: number) => void;
    onGirar: (indice: number, grados: number) => void;
    /** El asa de la esquina (8.6): el tamaño nuevo de la pieza de decoración. */
    onTamano?: (indice: number, ancho: number, alto: number) => void;
    texto: { indice: number; valor: string } | null;
    onEscribir: (indice: number) => void;
    onTexto: (valor: string) => void;
    onTextoHecho: () => void;
}

interface Props {
    nombre: string;
    ancho: number;
    alto: number;
    posiciones: readonly Posicion[];
    piezas: readonly PiezaEnSala[];
    recursos: readonly Recurso[];
    zoom: number;
    editando: boolean;
    elegido: string | null;
    elegidas?: ReadonlySet<string>;
    onElegir: (clave: string | null) => void;
    /** Empieza un arrastre (para que el editor guarde cómo estaba todo). */
    onEmpezarArrastre?: () => void;
    /** Desplazamiento desde donde empezó el arrastre, en la rejilla. */
    onArrastrar?: (claves: readonly string[], dx: number, dy: number) => void;
    /** El asa de la esquina de la mesa elegida (8.6): su tamaño nuevo. */
    onTamanoRecurso?: (id: string, ancho: number, alto: number) => void;
    /**
     * Se suelta lo arrastrado (mover, asa de tamaño, punto de una pared o giro), para la regla de la huella (G1200): las
     * claves de lo que cambió (recursos por su `resource_id`, piezas con `deco:<i>`).
     */
    onSoltar?: (modo: Arrastre['modo'], claves: readonly string[]) => void;
    decoracion?: EdicionDecoracion;
}

export type Arrastre =
    | { modo: 'grupo'; claves: string[]; x0: number; y0: number; movido: boolean }
    | { modo: 'tamano'; clave: string; ancho: number; alto: number; redonda: boolean; movido: boolean; x0: number; y0: number }
    | { modo: 'punto'; indice: number; i: number; movido: boolean; x0: number; y0: number }
    | { modo: 'giro'; indice: number; centro: { x: number; y: number }; movido: boolean; x0: number; y0: number };

interface Medidas { ancho: number; alto: number }

/** El arrastre sigue al puntero aunque salga de la pieza (sin captura, p. ej. en jsdom, se sigue por el lienzo). */
const capturar = (e: EventoPuntero) => {
    if (typeof e.currentTarget.setPointerCapture === 'function') {
        e.currentTarget.setPointerCapture(e.pointerId);
    }
};

export function Lienzo({ nombre, ancho, alto, posiciones, piezas, recursos, zoom, editando, elegido, elegidas, onElegir, onEmpezarArrastre, onArrastrar, onTamanoRecurso, onSoltar, decoracion }: Props) {
    const lienzo = useRef<HTMLDivElement>(null);
    const arrastre = useRef<Arrastre | null>(null);
    const trasArrastre = useRef(false);
    const [giro, setGiro] = useState<number | null>(null);
    // Ancho en pantalla del lienzo: las asas miden lo mismo en pantalla a cualquier zoom (44 px de zona táctil).
    const [anchoPx, setAnchoPx] = useState(0);
    useEffect(() => {
        const el = lienzo.current;
        if (el === null || typeof ResizeObserver === 'undefined') {
            return;
        }
        const ro = new ResizeObserver(() => { setAnchoPx(el.getBoundingClientRect().width); });
        ro.observe(el);
        return () => { ro.disconnect(); };
    }, []);
    const caja = cajaDe([...posiciones, ...piezas.map((p) => cajaDePieza(p.pieza))], ancho, alto);
    const pct = (v: number, total: number) => `${String((v / total) * 100)}%`;
    const conRejilla = decoracion?.conRejilla ?? false;
    const pared = editando ? decoracion?.pared ?? null : null;

    const escala = () => {
        const r = lienzo.current?.getBoundingClientRect();
        return r === undefined || r.width === 0 ? 1 : caja.ancho / r.width;
    };
    /** Del puntero a la rejilla del plano. */
    const enRejilla = (clientX: number, clientY: number): { x: number; y: number } => {
        const r = lienzo.current?.getBoundingClientRect();
        const k = escala();
        return { x: (clientX - (r?.left ?? 0)) * k, y: (clientY - (r?.top ?? 0)) * k };
    };
    const enPunto = (clientX: number, clientY: number): Punto => {
        const p = enRejilla(clientX, clientY);
        const a = (v: number) => (conRejilla ? ajustar(v, PASO_REJILLA) : Math.round(v));
        return [Math.min(caja.ancho, Math.max(0, a(p.x))), Math.min(caja.alto, Math.max(0, a(p.y)))];
    };

    const empezarGrupo = (e: EventoPuntero, clave: string) => {
        if (!editando || pared !== null || e.button > 0) {
            return;
        }
        const claves = elegidas?.has(clave) === true ? [...elegidas] : [clave];
        arrastre.current = { modo: 'grupo', claves, x0: e.clientX, y0: e.clientY, movido: false };
        capturar(e);
    };
    const empezarPunto = (e: EventoPuntero, indice: number, i: number, tramo: boolean) => {
        e.stopPropagation();
        if (tramo) {
            onEmpezarArrastre?.();
            decoracion?.onAnadirPunto(indice, i);
        }
        arrastre.current = { modo: 'punto', indice, i: tramo ? i + 1 : i, movido: tramo, x0: e.clientX, y0: e.clientY };
        capturar(e);
    };
    const empezarGiro = (e: EventoPuntero, indice: number, centro: { x: number; y: number }) => {
        e.stopPropagation();
        arrastre.current = { modo: 'giro', indice, centro, movido: false, x0: e.clientX, y0: e.clientY };
        capturar(e);
    };
    const empezarTamano = (e: EventoPuntero, clave: string, medidas: { ancho: number; alto: number }, redonda: boolean) => {
        e.stopPropagation();
        arrastre.current = { modo: 'tamano', clave, ...medidas, redonda, movido: false, x0: e.clientX, y0: e.clientY };
        capturar(e);
    };
    const mover = (e: EventoPuntero<HTMLDivElement>) => {
        const a = arrastre.current;
        if (a === null) {
            return;
        }
        const k = escala();
        const dx = (e.clientX - a.x0) * k;
        const dy = (e.clientY - a.y0) * k;
        if (!a.movido && Math.abs(dx) + Math.abs(dy) < 4) {
            return;
        }
        if (!a.movido) {
            onEmpezarArrastre?.();
        }
        a.movido = true;
        if (a.modo === 'grupo') {
            const r = (v: number) => (conRejilla ? ajustar(v, PASO_REJILLA) : Math.round(v));
            onArrastrar?.(a.claves, r(dx), r(dy));
        } else if (a.modo === 'tamano') {
            // 8.6: entre 20 y la sala, de 10 en 10 con la rejilla; las redondas siguen redondas.
            const t = tamanoDelAsa({ ancho: a.ancho, alto: a.alto }, dx, dy, { ancho, alto }, conRejilla, a.redonda);
            const i = indiceDeClave(a.clave);
            if (i === null) {
                onTamanoRecurso?.(a.clave, t.ancho, t.alto);
            } else {
                decoracion?.onTamano?.(i, t.ancho, t.alto);
            }
        } else if (a.modo === 'punto') {
            decoracion?.onMoverPunto(a.indice, a.i, enPunto(e.clientX, e.clientY));
        } else {
            const g = giroDelAsa(a.centro, enRejilla(e.clientX, e.clientY), conRejilla);
            setGiro(g);
            decoracion?.onGirar(a.indice, g);
        }
    };
    const soltar = () => {
        const a = arrastre.current;
        arrastre.current = null;
        setGiro(null);
        if (a === null) {
            return;
        }
        trasArrastre.current = a.movido && a.modo === 'grupo';
        if (a.modo === 'punto' && !a.movido) {
            decoracion?.onElegirPunto(a.i);
        }
        if (a.movido) {
            onSoltar?.(a.modo, a.modo === 'grupo' ? a.claves : a.modo === 'tamano' ? [a.clave] : [claveDeco(a.indice)]);
        }
    };
    const tocar = (clave: string) => {
        if (trasArrastre.current) {
            trasArrastre.current = false;
            return;
        }
        onElegir(clave);
    };

    const sola = elegido === null ? null : indiceDeClave(elegido);
    const elegidaSola = sola === null ? null : piezas.find((p) => p.indice === sola) ?? null;
    const enGrupo = new Set([...(elegidas ?? [])].map(indiceDeClave).filter((i): i is number => i !== null));
    const conAsas = editando && pared === null && (elegidas?.size ?? 0) === 0 ? elegidaSola : null;
    const mesaConAsa = editando && pared === null && (elegidas?.size ?? 0) === 0 && elegido !== null && sola === null ? posiciones.find((p) => p.resource_id === elegido) ?? null : null;
    const texto = editando ? decoracion?.texto ?? null : null;
    const piezaTexto = texto === null ? null : piezas.find((p) => p.indice === texto.indice) ?? null;
    const errorTexto = texto === null ? null : errorDeNombre(texto.valor, piezaTexto?.pieza.kind === 'text');
    const cajaTexto = piezaTexto === null ? null : cajaDePieza(piezaTexto.pieza);
    const k = anchoPx === 0 ? 1 : caja.ancho / anchoPx;

    return (
        <div className="rv-sl-plano" style={{ width: `${String(Math.round(zoom * 100))}%` }}>
            <div
                ref={lienzo}
                className={`rv-sl-plano__lienzo${editando ? ' rv-pl-lienzo--editando' : ''}`}
                role="group"
                aria-label={`Plano de ${nombre}`}
                style={{ aspectRatio: `${String(caja.ancho)} / ${String(caja.alto)}` }}
                onClick={(e) => { if (e.target === e.currentTarget) { onElegir(null); } }}
                onPointerMove={mover}
                onPointerUp={soltar}
                onPointerCancel={soltar}
            >
                <CapaDecoracion
                    ancho={caja.ancho}
                    alto={caja.alto}
                    piezas={piezas}
                    editando={editando && pared === null}
                    elegidas={enGrupo}
                    elegida={sola}
                    onPuntero={(e, i) => { empezarGrupo(e, claveDeco(i)); }}
                    onTocar={(i) => { tocar(claveDeco(i)); }}
                    onDobleToque={(i) => { if (piezas.find((p) => p.indice === i)?.pieza.kind === 'text') { decoracion?.onEscribir(i); } }}
                />
                {posiciones.map((p) => {
                    const r = recursos.find((x) => x.id === p.resource_id);
                    const codigo = p.label ?? r?.code ?? '';
                    const capacidad = r === undefined ? '' : `${String(r.current_terms.min_capacity)}–${String(r.current_terms.max_capacity)}`;
                    const marcado = elegido === p.resource_id || elegidas?.has(p.resource_id) === true;
                    return (
                        <button
                            key={p.resource_id}
                            type="button"
                            className={`rv-sl-recurso rv-sl-recurso--${p.shape}${marcado ? ' rv-sl-recurso--elegido' : ''}`}
                            aria-pressed={marcado}
                            aria-label={`${codigo}${capacidad === '' ? '' : ` · ${capacidad} personas`}`}
                            style={{
                                left: pct(p.x, caja.ancho),
                                top: pct(p.y, caja.alto),
                                width: pct(p.width, caja.ancho),
                                height: pct(p.height, caja.alto),
                                transform: p.rotation === undefined || p.rotation === 0 ? undefined : `rotate(${String(p.rotation)}deg)`,
                                touchAction: editando ? 'none' : undefined,
                            }}
                            onPointerDown={(e) => { empezarGrupo(e, p.resource_id); }}
                            onClick={() => { tocar(p.resource_id); }}
                        >
                            <strong>{codigo}</strong>
                            {capacidad !== '' && <small>{capacidad}</small>}
                        </button>
                    );
                })}
                {conAsas !== null && (
                    <Asas
                        pieza={conAsas}
                        caja={caja}
                        k={k}
                        puntoElegido={decoracion?.puntoElegido ?? null}
                        giro={giro}
                        onPunto={empezarPunto}
                        onGiro={empezarGiro}
                        onTamano={decoracion?.onTamano === undefined ? undefined : (e, medidas) => { empezarTamano(e, claveDeco(conAsas.indice), medidas, false); }}
                    />
                )}
                {mesaConAsa !== null && onTamanoRecurso !== undefined && (
                    // 8.6: el asa de la esquina de la mesa elegida, en la esquina de su anillo (las redondas, a 45°).
                    <svg className="rv-pl-capa rv-pl-capa--asas" viewBox={`0 0 ${String(caja.ancho)} ${String(caja.alto)}`} preserveAspectRatio="none">
                        <AsaTamano
                            {...(mesaConAsa.shape === 'round'
                                ? { x: mesaConAsa.x + mesaConAsa.width / 2 + (mesaConAsa.width / 2 + k * 7) * Math.SQRT1_2, y: mesaConAsa.y + mesaConAsa.height / 2 + (mesaConAsa.width / 2 + k * 7) * Math.SQRT1_2 }
                                : { x: mesaConAsa.x + mesaConAsa.width + k * 7, y: mesaConAsa.y + mesaConAsa.height + k * 7 })}
                            k={k}
                            onDown={(e) => { empezarTamano(e, mesaConAsa.resource_id, { ancho: mesaConAsa.width, alto: mesaConAsa.height }, mesaConAsa.shape === 'round'); }}
                        />
                    </svg>
                )}
                {pared !== null && decoracion !== undefined && (
                    <ParedADibujar
                        puntos={pared}
                        caja={caja}
                        k={k}
                        onToque={(e) => {
                            const p = enPunto(e.clientX, e.clientY);
                            const primero = pared[0];
                            if (primero !== undefined && pared.length >= 3 && Math.hypot(p[0] - primero[0], p[1] - primero[1]) <= 28 * k) {
                                decoracion.onCerrarPared();
                            } else {
                                decoracion.onPuntoPared(p);
                            }
                        }}
                        onCerrar={decoracion.onCerrarPared}
                    />
                )}
                {texto !== null && piezaTexto !== null && cajaTexto !== null && (
                    <div className="rv-pl-texto" style={{ left: pct(Math.max(0, cajaTexto.x), caja.ancho), top: pct(cajaTexto.y + cajaTexto.height + 12, caja.alto) }}>
                        <span className="rv-pl-texto__fila">
                            <input
                                aria-label={piezaTexto.pieza.kind === 'text' ? 'Texto en el plano' : 'Nombre en el plano'}
                                value={texto.valor}
                                autoFocus
                                aria-invalid={errorTexto !== null}
                                onChange={(e) => { decoracion?.onTexto(e.target.value); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' && errorTexto === null) { e.preventDefault(); decoracion?.onTextoHecho(); } }}
                            />
                            <button type="button" className="rv-b rv-b-pri" disabled={errorTexto !== null} onClick={() => { decoracion?.onTextoHecho(); }}><Icono nombre="check" /><span>Hecho</span></button>
                        </span>
                        <small className={errorTexto === null ? undefined : 'rv-pl-texto__error'} role={errorTexto === null ? undefined : 'alert'}>
                            <span>{errorTexto ?? 'La letra crece o mengua con el rectángulo.'}</span>
                            <span>{`${String(texto.valor.length)}/${String(MAX_TEXTO)}`}</span>
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Asa de tamaño (8.6, editor): punto blanco con borde rojo dentro de una zona táctil de 44 px de pantalla; arrastrarla
 * cambia el ancho y el fondo. Como en el dibujo, no la lee el lector de pantalla: el tamaño también se escribe en los campos.
 */
function AsaTamano({ x, y, k, onDown }: { x: number; y: number; k: number; onDown: (e: EventoPuntero) => void }) {
    return (
        <g className="rv-pl-asa rv-pl-asa--tamano" aria-hidden="true" onPointerDown={onDown}>
            <circle cx={x} cy={y} r={k * 22} style={{ fill: 'transparent' }} />
            <circle cx={x} cy={y} r={k * 9} style={{ fill: '#fff', stroke: 'var(--rv-red)', strokeWidth: k * 3 }} />
        </g>
    );
}

/** Asas de la pieza elegida: los puntos de la pared (con el «+» de cada tramo) o las asas de giro y de tamaño. */
function Asas({ pieza, caja, k, puntoElegido, giro, onPunto, onGiro, onTamano }: {
    pieza: PiezaEnSala;
    caja: Medidas;
    k: number;
    puntoElegido: number | null;
    giro: number | null;
    onPunto: (e: EventoPuntero, indice: number, i: number, tramo: boolean) => void;
    onGiro: (e: EventoPuntero, indice: number, centro: { x: number; y: number }) => void;
    onTamano?: (e: EventoPuntero, medidas: { ancho: number; alto: number }) => void;
}) {
    const d = pieza.pieza;
    const pts = puntosDe(d);
    const r = (px: number) => px * k;
    const vista = `0 0 ${String(caja.ancho)} ${String(caja.alto)}`;
    if (pts !== null) {
        const abiertos = puntosAbiertos(pts);
        const cerrada = estaCerrada(pts);
        const tramos = cerrada ? abiertos.length : abiertos.length - 1;
        const caben = cabeOtroPunto(abiertos.length, cerrada);
        return (
            <svg className="rv-pl-capa rv-pl-capa--asas" viewBox={vista} preserveAspectRatio="none">
                {caben && Array.from({ length: Math.max(0, tramos) }, (_, i) => {
                    const a = abiertos[i] ?? [0, 0];
                    const b = abiertos[(i + 1) % abiertos.length] ?? [0, 0];
                    if (Math.hypot(b[0] - a[0], b[1] - a[1]) < r(76)) {
                        return null;
                    }
                    const mx = (a[0] + b[0]) / 2;
                    const my = (a[1] + b[1]) / 2;
                    return (
                        <g key={`m${String(i)}`} className="rv-pl-asa rv-pl-asa--tramo" role="button" aria-label={`Añadir un punto en el tramo ${String(i + 1)}`} onPointerDown={(e) => { onPunto(e, pieza.indice, i, true); }}>
                            <circle cx={mx} cy={my} r={r(20)} style={{ fill: 'transparent' }} />
                            <circle cx={mx} cy={my} r={r(9)} style={{ fill: '#fff', stroke: 'var(--rv-ink)', strokeWidth: r(2) }} />
                            <path d={`M${String(mx - r(4.5))} ${String(my)}H${String(mx + r(4.5))}M${String(mx)} ${String(my - r(4.5))}V${String(my + r(4.5))}`} style={{ stroke: 'var(--rv-ink)', strokeWidth: r(2), strokeLinecap: 'round' }} />
                        </g>
                    );
                })}
                {abiertos.map((q, i) => {
                    const on = puntoElegido === i;
                    return (
                        <g key={`p${String(i)}`} className="rv-pl-asa" role="button" aria-label={`Punto ${String(i + 1)}`} aria-pressed={on} onPointerDown={(e) => { onPunto(e, pieza.indice, i, false); }}>
                            <circle cx={q[0]} cy={q[1]} r={r(22)} style={{ fill: 'transparent' }} />
                            <circle cx={q[0]} cy={q[1]} r={r(on ? 12 : 10)} style={{ fill: on ? 'var(--rv-red)' : '#fff', stroke: 'var(--rv-red)', strokeWidth: r(3) }} />
                        </g>
                    );
                })}
            </svg>
        );
    }
    const g = d.geometry;
    const x = g.x ?? 0;
    const y = g.y ?? 0;
    const w = g.width ?? 0;
    const h = g.height ?? 0;
    const centro = { x: x + w / 2, y: y + h / 2 };
    const angulo = ((giro ?? g.rotation ?? 0) * Math.PI) / 180;
    const cerca = h / 2 + 8;
    const lejos = cerca + r(30);
    const ax = centro.x + lejos * Math.sin(angulo);
    const ay = centro.y - lejos * Math.cos(angulo);
    return (
        <svg className="rv-pl-capa rv-pl-capa--asas" viewBox={vista} preserveAspectRatio="none">
            <line x1={centro.x + cerca * Math.sin(angulo)} y1={centro.y - cerca * Math.cos(angulo)} x2={ax} y2={ay} style={{ stroke: 'var(--rv-red)', strokeWidth: r(3) }} />
            <g className="rv-pl-asa rv-pl-asa--giro" role="button" aria-label="Girar la pieza" onPointerDown={(e) => { onGiro(e, pieza.indice, centro); }}>
                <circle cx={ax} cy={ay} r={r(22)} style={{ fill: 'transparent' }} />
                <circle cx={ax} cy={ay} r={r(11)} style={{ fill: 'var(--rv-red)', stroke: '#fff', strokeWidth: r(3) }} />
            </g>
            {onTamano !== undefined && <AsaTamano x={x + w + 8} y={y + h + 8} k={k} onDown={(e) => { onTamano(e, { ancho: w, alto: h }); }} />}
            {giro !== null && <text x={ax + r(20)} y={ay} dominantBaseline="central" className="rv-pl-capa__rotulo" style={{ fontSize: r(15), fill: 'var(--rv-red)' }}>{`${String(giro)}°`}</text>}
        </svg>
    );
}

/** La pared a medio dibujar: recoge los toques de todo el lienzo; desde tres puntos, el primero dice «Toca aquí para cerrarla». */
function ParedADibujar({ puntos, caja, k, onToque, onCerrar }: { puntos: readonly Punto[]; caja: Medidas; k: number; onToque: (e: EventoPuntero<SVGSVGElement>) => void; onCerrar: () => void }) {
    const primero = puntos[0];
    return (
        <svg
            className="rv-pl-capa rv-pl-capa--pared"
            viewBox={`0 0 ${String(caja.ancho)} ${String(caja.alto)}`}
            preserveAspectRatio="none"
            role="application"
            aria-label="Toca en el plano para poner los puntos de la pared"
            onPointerUp={onToque}
        >
            {puntos.length > 1 && <path d={`M${puntos.map((q) => `${String(q[0])} ${String(q[1])}`).join('L')}`} style={{ fill: 'none', stroke: 'var(--rv-ink)', strokeWidth: 14, strokeLinejoin: 'miter', strokeLinecap: 'square' }} />}
            {puntos.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={k * 9} style={{ fill: i === puntos.length - 1 ? 'var(--rv-ink)' : '#fff', stroke: 'var(--rv-ink)', strokeWidth: k * 3 }} />)}
            {primero !== undefined && puntos.length >= 3 && (
                <g role="button" aria-label="Toca aquí para cerrarla" onPointerUp={(e) => { e.stopPropagation(); onCerrar(); }}>
                    <circle cx={primero[0]} cy={primero[1]} r={k * 14} style={{ fill: 'var(--rv-red)' }} />
                    <text x={primero[0]} y={primero[1] - k * 30} textAnchor="middle" className="rv-pl-capa__rotulo" style={{ fontSize: k * 14, fill: 'var(--rv-red)' }}>Toca aquí para cerrarla</text>
                </g>
            )}
        </svg>
    );
}
